import { CheckCircle, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UseCaseDetail, CadrageReadWithTeam } from '../../../types';
import QualificationSlider from '../Qualification/QualificationSlider';
import ScoreProgressBar from '../../Common/ScoreProgressBar';
import MarkdownEditor from '../../Common/MarkdownEditor';
import MarkdownDisplay from '../../Common/MarkdownDisplay';
import CompletionIndicator from '../../Common/CompletionIndicator';

import { Button } from '../../Common/button';

const isFilledStr = (s: string | null | undefined) => !!(s && s.trim().length > 0);
const isFilledScore = (n: number | null | undefined) => typeof n === 'number' && n > 0;
const isFilledArr = (a: unknown[] | null | undefined) => Array.isArray(a) && a.length > 0;

interface UseCaseOverviewTabProps {
  useCase: UseCaseDetail;
  editedShortDescription: string;
  editedDescription: string;
  editedRecommendation: string;
  editedStatus: string;
  onShortDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onRecommendationChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onStatusChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  edited_value_score: number;
  edited_complexity_score: number;
  on_value_score_change: (value: number) => void;
  on_complexity_score_change: (value: number) => void;
  linkedCadrages?: CadrageReadWithTeam[];
  onSave: () => Promise<void>;
  onDelete?: () => void;
  isDeleting?: boolean;
}

const UseCaseOverviewTab: React.FC<UseCaseOverviewTabProps> = ({
  useCase,
  editedShortDescription,
  editedDescription,
  editedRecommendation,
  editedStatus,
  onShortDescriptionChange,
  onDescriptionChange,
  onRecommendationChange,
  onStatusChange,
  edited_value_score,
  edited_complexity_score,
  on_value_score_change,
  on_complexity_score_change,
  linkedCadrages = [],
  onSave,
  onDelete,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const activeCadrage = linkedCadrages.find(c => c.is_active);

  const completionFields = [
    { label: t('useCaseOverview.shortDescription', 'Description courte'), filled: isFilledStr(useCase.short_description) },
    { label: t('useCaseOverview.detailedDescription', 'Description Détaillée'), filled: isFilledStr(useCase.description) },
    { label: t('useCaseOverview.recommendation', 'Enjeux'), filled: isFilledStr(useCase.recommendation) },
    { label: t('newUseCase.businessNeedLabel', 'Besoin métier'), filled: isFilledStr(useCase.business_need) },
    { label: t('global.businessValue', 'Valeur Métier'), filled: isFilledScore(useCase.value_score) },
    { label: t('global.technicalComplexity', 'Complexité Technique'), filled: isFilledScore(useCase.complexity_score) },
    { label: t('global.category', 'Catégorie'), filled: isFilledStr(useCase.category) },
    { label: t('newUseCase.involvedToolsLabel', 'Outils impliqués'), filled: isFilledArr(useCase.involved_tools) },
  ];

  const handleSave = async () => {
    await onSave();
    setIsEditing(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      {!isEditing && (
        <CompletionIndicator fields={completionFields} />
      )}
      <div className="flex items-center justify-between border-b border-border pb-2">
        <h2 className="text-xl font-semibold text-foreground">{t('global.overview')}</h2>
        {!isEditing ? (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" />
              {t('global.edit')}
            </Button>
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onDelete}
                disabled={isDeleting}
                aria-label={t('global.delete', 'Supprimer')}
                title={t('global.delete', 'Supprimer')}
                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={handleSave}>
              {t('global.save')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(false)}>
              {t('global.cancel')}
            </Button>
          </div>
        )}
      </div>

      {isEditing && (
        <div>
          <h3 className="text-lg font-medium text-foreground mb-2">{t('global.status')}</h3>
          <select
            value={editedStatus}
            onChange={onStatusChange}
            className="block w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-foreground shadow-sm focus:border-ring focus:ring-ring sm:text-sm"
          >
            <option value="Identified">{t('useCaseOverview.statusIdentified')}</option>
            <option value="Qualified">{t('useCaseOverview.statusQualified')}</option>
            <option value="Prioritized">{t('useCaseOverview.statusPrioritized')}</option>
            <option value="In Progress">{t('useCaseOverview.statusInProgress')}</option>
            <option value="Completed">{t('useCaseOverview.statusCompleted')}</option>
            <option value="Abandoned">{t('useCaseOverview.statusAbandoned')}</option>
          </select>
        </div>
      )}

      {/* Lien vers le cadrage actif */}
      {activeCadrage && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-sm font-medium text-muted-foreground">{t('useCaseOverview.linkedCadrage')}</h3>
                <span className="flex items-center space-x-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  <CheckCircle className="h-3 w-3" />
                  <span>{t('useCaseOverview.active')}</span>
                </span>
              </div>
              <p className="text-lg font-semibold text-foreground mb-1">{activeCadrage.name}</p>
              {activeCadrage.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{activeCadrage.description}</p>
              )}
              <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-2">
                <span>{t('useCaseOverview.version')} {activeCadrage.version}</span>
                <span className="px-2 py-0.5 bg-primary/10 text-primary rounded">
                  {activeCadrage.status
                    ? t(`cadrage.general.statuses.${activeCadrage.status}`, activeCadrage.status)
                    : activeCadrage.status}
                </span>
              </div>
            </div>
            <Link
              to={`/cadrages/${activeCadrage.id}`}
              className="ml-4 flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
            >
              <span>{t('useCaseOverview.viewCadrage')}</span>
              <ExternalLink className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Résumé Exécutif */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">{t('global.summary')}</h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedShortDescription}
            onChange={(val) => onShortDescriptionChange({ target: { value: val || '' } } as React.ChangeEvent<HTMLTextAreaElement>)}
            preview="live"
            height={120}
            placeholder={t('useCaseOverview.summaryPlaceholder')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.short_description} emptyMessage={t('global.notDefined')} />
        )}
      </div>

      {/* Description Détaillée */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">{t('useCaseOverview.detailedDescription')}</h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedDescription}
            onChange={(val) => onDescriptionChange({ target: { value: val || '' } } as React.ChangeEvent<HTMLTextAreaElement>)}
            preview="live"
            height={240}
            placeholder={t('useCaseOverview.descriptionPlaceholder')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.description} emptyMessage={t('global.notDefined')} />
        )}
      </div>

      {/* Recommandation */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">{t('useCaseOverview.recommendation')}</h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedRecommendation}
            onChange={(val) => onRecommendationChange({ target: { value: val || '' } } as React.ChangeEvent<HTMLTextAreaElement>)}
            preview="live"
            height={160}
            placeholder={t('useCaseOverview.recommendationPlaceholder')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.recommendation} emptyMessage={t('global.notDefined')} />
        )}
      </div>

      {/* Indicateurs Clés (Non éditable pour l'instant, sliders ajoutés plus tard) */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">{t('useCaseOverview.keyIndicators')}</h3>
        {isEditing ? (
          <>
            <QualificationSlider
              label={t('global.businessValue')}
              min={1}
              max={10}
              value={edited_value_score} // Correction: camelCase -> snake_case
              onChange={on_value_score_change} // Correction: camelCase -> snake_case
              id="value-score-slider"
            />
            <QualificationSlider
              label={t('global.technicalComplexity')}
              min={1}
              max={10}
              value={edited_complexity_score} // Correction: camelCase -> snake_case
              onChange={on_complexity_score_change} // Correction: camelCase -> snake_case
              id="complexity-score-slider"
            />
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <ScoreProgressBar
              label={t('global.businessValue')}
              value={useCase.value_score ?? null}
              max={10}
              barColor="bg-green-600"
            />
            <ScoreProgressBar
              label={t('global.technicalComplexity')}
              value={useCase.complexity_score ?? null}
              max={10}
              barColor="bg-amber-500"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UseCaseOverviewTab;
